// Recipe Book hook — fresh, dedicated read path for the profile
// Recipe Book overlay. Deliberately separate from useUserRecipes
// because the latter has been buggy in ways the user has been
// chasing for a while (stale 12h localStorage cache + realtime merge
// edge cases + family-shared visibility); rather than diagnose every
// failure mode, this hook is a clean-room: direct DB query, no
// localStorage cache, scoped to the viewer's own rows only, with the
// same guarded UPDATE merge that useUserRecipes uses defensively.
//
// The CreateMenu picker keeps using useUserRecipes — this hook is
// only for the new Recipe Book surface. If a regression shows up
// here we can debug a 100-line file instead of a 400-line one.
//
// Returns:
//   recipes        — array of { id, userId, slug, source, recipe,
//                    shared, createdAt, updatedAt } sorted newest-first
//   loading        — boolean. True only on first load.
//   error          — string | null
//   refresh()      — re-fetch from DB. Called on overlay open so the
//                    user always sees fresh state when they enter.
//   deleteRecipe(id)
//   setSharing(id, { shared })

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";
import { applyVersionSnapshot } from "./recipeEdits";
import { readCache, writeCache } from "./useCachedQuery";

// Stale-while-revalidate cache. This is NOT the old TTL-gated cache
// that got ripped out (it trusted stale data within 12h and skipped
// the network, so a just-saved recipe could stay invisible for hours).
// Here the cache is ONLY an instant-paint placeholder: we hydrate from
// it immediately, but ALWAYS hit the network on mount and overwrite —
// so the cache is never authoritative and can't hide a fresh write.
// Read with Infinity TTL: age never disqualifies the placeholder.
const CACHE_KEY = (uid) => `recipeBook:v1:${uid}`;
const hydrateCache = (uid) => (uid ? readCache(CACHE_KEY(uid), Infinity) : null) || [];

function fromDb(row) {
  return {
    id:             row.id,
    userId:         row.user_id,
    slug:           row.slug,
    source:         row.source,
    recipe:         row.recipe || {},
    shared:         row.shared === true,
    // Community leaderboard opt-in (migration 0236).
    communityPublished:   row.community_published === true,
    communityPublishedAt: row.community_published_at || null,
    isFavorite:     row.is_favorite === true,
    personalNotes:  row.personal_notes || "",
    tags:           Array.isArray(row.tags) ? row.tags : [],
    createdAt:      row.created_at,
    updatedAt:      row.updated_at,
  };
}

export function useRecipeBook(userId) {
  // Hydrate instantly from the local cache so the book paints with
  // content on first frame instead of a spinner / blank page.
  const [recipes, setRecipes] = useState(() => hydrateCache(userId));
  const [loading, setLoading] = useState(() => !(userId && readCache(CACHE_KEY(userId), Infinity)));
  const [error, setError] = useState(null);
  // Mirror "do we have something on screen" so refresh can decide
  // whether to show a spinner (only when there's nothing yet).
  const hasContentRef = useRef(recipes.length > 0);
  useEffect(() => { hasContentRef.current = recipes.length > 0; }, [recipes]);
  // Stash the setter in a ref so the realtime subscription effect
  // doesn't have to depend on it (dependency churn would tear down
  // the realtime channel on every render).
  const setRecipesRef = useRef(setRecipes);
  useEffect(() => { setRecipesRef.current = setRecipes; }, []);

  const refresh = useCallback(async () => {
    if (!userId) {
      setRecipes([]);
      setLoading(false);
      return;
    }
    // Instant paint on a user switch: show that user's cached rows
    // immediately, spinner only when we have nothing to show. The
    // network call below ALWAYS runs and overwrites — cache is a
    // placeholder, never the source of truth.
    const cached = readCache(CACHE_KEY(userId), Infinity);
    if (cached && !hasContentRef.current) setRecipes(cached);
    if (!cached && !hasContentRef.current) setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("user_recipes")
      .select("*")
      .eq("user_id", userId)        // viewer's own rows only
      .order("created_at", { ascending: false });
    if (err) {
      // eslint-disable-next-line no-console
      console.error("[recipeBook] load failed:", err);
      setError(err.message || "load failed");
      // Keep whatever we already have (cache / prior fetch) on screen
      // — clobbering with [] on a transient network blip would blank
      // the book. The cache write-through below skips on error too.
    } else {
      setRecipes((data || []).map(fromDb));
    }
    setLoading(false);
  }, [userId]);

  // Write-through: persist the list to the cache on every successful
  // state — fetch results, mutations (save/edit/delete/share), and
  // realtime all flow through `recipes`, so one effect covers them.
  // Skip on error (don't overwrite a good cache with a failed load)
  // and while loading with nothing yet.
  useEffect(() => {
    if (!userId || error) return;
    if (loading && recipes.length === 0) return;
    writeCache(CACHE_KEY(userId), recipes);
  }, [recipes, userId, error, loading]);

  // Initial load + reload when userId changes.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Realtime — keep the list in sync with INSERTs (new saves from
  // any tab the user has open), UPDATEs (share toggles, edits), and
  // DELETEs. Filtered server-side to user_id = viewer.
  useEffect(() => {
    if (!userId) return;
    const ch = safeChannel(`rt:recipeBook:${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_recipes", filter: `user_id=eq.${userId}` },
        (payload) => {
          const apply = setRecipesRef.current;
          if (payload.eventType === "INSERT") {
            const row = fromDb(payload.new);
            apply((prev) => prev.some((r) => r.id === row.id) ? prev : [row, ...prev]);
          } else if (payload.eventType === "UPDATE") {
            // MERGE rather than replace. Realtime UPDATE payloads can
            // arrive with an empty `recipe` jsonb field under some
            // partial-replication conditions (the old useUserRecipes
            // bug). Preserve existing recipe when incoming is empty.
            const incoming = fromDb(payload.new);
            apply((prev) => prev.map((r) => {
              if (r.id !== incoming.id) return r;
              const mergedRecipe =
                incoming.recipe && Object.keys(incoming.recipe).length > 0
                  ? incoming.recipe
                  : r.recipe;
              return {
                ...r,
                ...incoming,
                recipe: mergedRecipe,
                slug:   incoming.slug   || r.slug,
                source: incoming.source || r.source,
                userId: incoming.userId || r.userId,
              };
            }));
          } else if (payload.eventType === "DELETE") {
            const id = payload.old?.id;
            if (!id) return;
            apply((prev) => prev.filter((r) => r.id !== id));
          }
        },
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [userId]);

  // Save a new recipe to the book. `source` is "custom" by default
  // (the book's IMPORT FROM PASTE flow uses this; AI drafts come in
  // through a separate path that already stamps source="ai"). Slug
  // is generated from the title with a unique suffix on collision
  // within this user's existing rows. Optimistic mirror so the new
  // row appears in the book immediately without waiting on realtime.
  const saveRecipe = useCallback(async (recipe, source = "custom") => {
    if (!userId) throw new Error("not signed in");
    if (!recipe) throw new Error("recipe required");
    const baseSlug = String(recipe.slug || recipe.title || "recipe")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "recipe";
    const taken = new Set(recipes.filter((r) => r.userId === userId).map((r) => r.slug));
    let slug = baseSlug;
    let n = 2;
    while (taken.has(slug)) { slug = `${baseSlug}-${n}`; n++; }
    const stamped = { ...recipe, slug };
    // Transient cook-routing field (serving-count seed from schedule /
    // book entry points) — never stored recipe content.
    delete stamped._initialServings;
    const row = {
      user_id: userId,
      slug,
      source,
      recipe: stamped,
      shared: false,
    };
    const { data, error: err } = await supabase
      .from("user_recipes")
      .insert(row)
      .select()
      .single();
    if (err) {
      // eslint-disable-next-line no-console
      console.error("[recipeBook] save failed:", err);
      throw new Error(err.message || "save failed");
    }
    if (data?.id) {
      const fresh = fromDb(data);
      setRecipes((prev) => prev.some((r) => r.id === fresh.id) ? prev : [fresh, ...prev]);
    }
    return fromDb(data);
  }, [userId, recipes]);

  const deleteRecipe = useCallback(async (id) => {
    if (!id) return;
    // Optimistic remove — the row vanishes from the book the moment
    // the user confirms delete, no waiting on the round-trip. Realtime
    // DELETE event is deduped by the id-based filter.
    const snapshot = recipes;
    setRecipes((prev) => prev.filter((r) => r.id !== id));
    const { error: err } = await supabase
      .from("user_recipes")
      .delete()
      .eq("id", id);
    if (err) {
      // Roll back if the server rejected.
      // eslint-disable-next-line no-console
      console.error("[recipeBook] delete failed:", err);
      setRecipes(snapshot);
      throw new Error(err.message || "delete failed");
    }
  }, [recipes]);

  const setSharing = useCallback(async (id, { shared } = {}) => {
    if (!id || typeof shared !== "boolean") return;
    const snapshot = recipes;
    setRecipes((prev) => prev.map((r) =>
      r.id === id ? { ...r, shared } : r
    ));
    const { error: err } = await supabase
      .from("user_recipes")
      .update({ shared })
      .eq("id", id);
    if (err) {
      // eslint-disable-next-line no-console
      console.error("[recipeBook] setSharing failed:", err);
      setRecipes(snapshot);
      throw new Error(err.message || "update failed");
    }
  }, [recipes]);

  // Community publish toggle (migration 0236). Separate from family
  // sharing — flipping this puts the recipe on the public leaderboard
  // and lets non-family users cook it (cooks count as votes via
  // cook_logs.community_recipe_id). Author-only via RLS.
  const setCommunityPublished = useCallback(async (id, communityPublished) => {
    if (!id || typeof communityPublished !== "boolean") return;
    const snapshot = recipes;
    const stampedAt = communityPublished ? new Date().toISOString() : null;
    setRecipes((prev) => prev.map((r) =>
      r.id === id ? { ...r, communityPublished, communityPublishedAt: stampedAt } : r
    ));
    const { error: err } = await supabase
      .from("user_recipes")
      .update({
        community_published:    communityPublished,
        community_published_at: stampedAt,
      })
      .eq("id", id);
    if (err) {
      console.error("[recipeBook] setCommunityPublished failed:", err);
      setRecipes(snapshot);
      throw new Error(err.message || "update failed");
    }
  }, [recipes]);

  // Favorite toggle. Optimistic-then-rollback, same shape as
  // setSharing. The partial index on (user_id) WHERE is_favorite
  // makes the FAVORITES filter fast at scale.
  const setFavorite = useCallback(async (id, isFavorite) => {
    if (!id || typeof isFavorite !== "boolean") return;
    const snapshot = recipes;
    setRecipes((prev) => prev.map((r) =>
      r.id === id ? { ...r, isFavorite } : r,
    ));
    const { error: err } = await supabase
      .from("user_recipes")
      .update({ is_favorite: isFavorite })
      .eq("id", id);
    if (err) {
      // eslint-disable-next-line no-console
      console.error("[recipeBook] setFavorite failed:", err);
      setRecipes(snapshot);
      throw new Error(err.message || "update failed");
    }
  }, [recipes]);

  // Personal notes editor — optimistic update, no rollback on error
  // (the user is mid-typing; reverting their text would be hostile).
  // Caller is expected to debounce keystrokes upstream so we don't
  // fire a round-trip per character. Empty string is stored as null
  // so the column reads as "no note set" rather than "empty note set."
  const setNotes = useCallback(async (id, notes) => {
    if (!id) return;
    const trimmed = typeof notes === "string" ? notes : "";
    setRecipes((prev) => prev.map((r) =>
      r.id === id ? { ...r, personalNotes: trimmed } : r,
    ));
    const { error: err } = await supabase
      .from("user_recipes")
      .update({ personal_notes: trimmed.length > 0 ? trimmed : null })
      .eq("id", id);
    if (err) {
      // eslint-disable-next-line no-console
      console.error("[recipeBook] setNotes failed:", err);
      // Don't roll back — user's typing is the source of truth.
    }
  }, []);

  // Title rename — read-modify-write the recipe JSONB. Title lives
  // INSIDE the recipe object (no top-level title column on the row),
  // so we can't update a single column. We also leave the row's slug
  // alone: slugs are URL-stable identifiers tied to scheduled_meals,
  // cook_log_refs, and shared links — renaming the title keeps the
  // same slug so existing references stay intact. The book just shows
  // the new prose title; all the underlying joins keep working.
  //
  // Validation: empty or whitespace-only titles are rejected. Bigger
  // titles get capped at 200 chars to keep the page header readable.
  const setTitle = useCallback(async (id, nextTitle) => {
    if (!id) return;
    const trimmed = String(nextTitle || "").trim().slice(0, 200);
    if (!trimmed) return;
    const target = recipes.find((r) => r.id === id);
    if (!target || !target.recipe) return;
    if (target.recipe.title === trimmed) return;   // no-op short-circuit
    const snapshot = recipes;
    setRecipes((prev) => prev.map((r) =>
      r.id === id
        ? { ...r, recipe: { ...r.recipe, title: trimmed } }
        : r,
    ));
    const updatedRecipe = { ...target.recipe, title: trimmed };
    const { error: err } = await supabase
      .from("user_recipes")
      .update({ recipe: updatedRecipe })
      .eq("id", id);
    if (err) {
      // eslint-disable-next-line no-console
      console.error("[recipeBook] setTitle failed:", err);
      setRecipes(snapshot);
      throw new Error(err.message || "rename failed");
    }
  }, [recipes]);

  // Pairings cache — persists Claude's wine/cocktail/non-alcoholic
  // suggestion onto the recipe JSONB so the user pays at most one AI
  // call per recipe. Mirror of setTitle: read-modify-write the whole
  // recipe object, optimistic local update, rollback on error.
  // Caller passes the full `pairings` object (or null to clear).
  const setPairings = useCallback(async (id, pairings) => {
    if (!id) return;
    const target = recipes.find((r) => r.id === id);
    if (!target || !target.recipe) return;
    const snapshot = recipes;
    const nextRecipe = { ...target.recipe, pairings };
    setRecipes((prev) => prev.map((r) =>
      r.id === id ? { ...r, recipe: nextRecipe } : r,
    ));
    const { error: err } = await supabase
      .from("user_recipes")
      .update({ recipe: nextRecipe })
      .eq("id", id);
    if (err) {
      // eslint-disable-next-line no-console
      console.error("[recipeBook] setPairings failed:", err);
      setRecipes(snapshot);
      throw new Error(err.message || "save failed");
    }
  }, [recipes]);

  // Side-dish links — the main recipe's `sideDishes: [{ slug, title }]`
  // array (side-dish generation flow). Mirror of setPairings: whole-
  // jsonb write, optimistic, rollback. Deliberately NOT
  // updateRecipeContent — that path snapshots a "past version" on
  // every write, and a link add is metadata, not a content edit.
  const setSideDishes = useCallback(async (id, sideDishes) => {
    if (!id || !Array.isArray(sideDishes)) return;
    const target = recipes.find((r) => r.id === id);
    if (!target || !target.recipe) return;
    const prevRecipe = target.recipe;
    const nextRecipe = { ...prevRecipe, sideDishes };
    setRecipes((prev) => prev.map((r) =>
      r.id === id ? { ...r, recipe: nextRecipe } : r,
    ));
    const { error: err } = await supabase
      .from("user_recipes")
      .update({ recipe: nextRecipe })
      .eq("id", id);
    if (err) {
      // eslint-disable-next-line no-console
      console.error("[recipeBook] setSideDishes failed:", err);
      // Roll back ONLY this row, functionally — the array may have
      // gained rows since this closure was created (the side-dish
      // flow prepends a stub right before calling this); restoring a
      // whole stale snapshot would erase them.
      setRecipes((prev) => prev.map((r) =>
        r.id === id ? { ...r, recipe: prevRecipe } : r,
      ));
      throw new Error(err.message || "save failed");
    }
  }, [recipes]);

  // Full recipe-content replacement — the in-place save path for the
  // edit mode / AI revise features. Mirror of setPairings: optimistic
  // local swap, whole-JSONB write, rollback on error. The row's SLUG
  // NEVER changes here — scheduled_meals, cook_logs, kid_recipes, and
  // shared links all join on it (same rationale as setTitle above) —
  // so the incoming recipe gets the row's existing slug re-stamped
  // regardless of what the caller passed.
  const updateRecipeContent = useCallback(async (id, nextRecipe) => {
    if (!id || !nextRecipe || typeof nextRecipe !== "object") return;
    const target = recipes.find((r) => r.id === id);
    if (!target) return;
    // Snapshot the prior version before overwriting so edits are never
    // destructive (vegan/vegetarian/etc. keep the original one tap
    // away). Stored in recipe.versions[]; no migration.
    const stamped = applyVersionSnapshot(target.recipe || {}, { ...nextRecipe, slug: target.slug });
    const snapshot = recipes;
    setRecipes((prev) => prev.map((r) =>
      r.id === id ? { ...r, recipe: stamped } : r,
    ));
    const { error: err } = await supabase
      .from("user_recipes")
      .update({ recipe: stamped })
      .eq("id", id);
    if (err) {
      // eslint-disable-next-line no-console
      console.error("[recipeBook] updateRecipeContent failed:", err);
      setRecipes(snapshot);
      throw new Error(err.message || "save failed");
    }
  }, [recipes]);

  // Tags replacement — caller passes the new full tag array (not a
  // delta). Tags are normalized lowercase + trimmed + de-duped here
  // so casing inconsistencies ("Weeknight" vs. "weeknight") don't
  // fragment the user's taxonomy.
  const setTags = useCallback(async (id, tags) => {
    if (!id || !Array.isArray(tags)) return;
    const cleaned = Array.from(new Set(
      tags
        .map((t) => String(t || "").trim().toLowerCase())
        .filter(Boolean),
    )).slice(0, 12);
    const snapshot = recipes;
    setRecipes((prev) => prev.map((r) =>
      r.id === id ? { ...r, tags: cleaned } : r,
    ));
    const { error: err } = await supabase
      .from("user_recipes")
      .update({ tags: cleaned.length > 0 ? cleaned : null })
      .eq("id", id);
    if (err) {
      // eslint-disable-next-line no-console
      console.error("[recipeBook] setTags failed:", err);
      setRecipes(snapshot);
      throw new Error(err.message || "update failed");
    }
  }, [recipes]);

  return {
    recipes, loading, error, refresh,
    saveRecipe,
    deleteRecipe, setSharing, setCommunityPublished,
    setFavorite, setNotes, setTags, setTitle, setPairings,
    setSideDishes,
    updateRecipeContent,
  };
}
